import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { showToast } from '@klpkonsern/ui';
import type { Aktivitet, FormField } from '@klp/formgenerator/interfaces';
import { CamundaFields } from '@forvalter/utils/CamundaUtils';
import {
    useSendNotificationWithCommentMutation,
    updateLeverandorProcessFormfieldsCache,
} from '../../../services/rtkApi';
import { useRolleKommentar } from './useRolleKommentar';

type UseReplyToRolleKommentarParams = {
    activity: Aktivitet;
    prosessInstansId: string;
    culture: string;
};

export const useReplyToRolleKommentar = ({
    activity,
    prosessInstansId,
    culture,
}: UseReplyToRolleKommentarParams) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { attachComment } = useRolleKommentar(activity);

    const [sendNotification, { isLoading: isSending }] =
        useSendNotificationWithCommentMutation();

    const isLoading = isSending;

    const replyToComment = useCallback(
        async (
            rolle: string,
            comment: string,
            afterIndex?: number,
            depth = 1,
        ) => {
            if (!activity.id || !rolle || !comment.trim()) return;

            try {
                const rolleKommentarer = attachComment(
                    comment,
                    rolle,
                    depth,
                    afterIndex,
                );

                const updatedFormFields: Array<FormField> = (
                    activity.formFields ?? []
                ).map((field) =>
                    field.id === CamundaFields.ROLLE_KOMMENTARER
                        ? { ...field, value: rolleKommentarer }
                        : field,
                );

                await sendNotification({
                    prosessInstansId,
                    rolle,
                    comment,
                    rolleKommentarer,
                }).unwrap();

                dispatch(
                    updateLeverandorProcessFormfieldsCache(
                        prosessInstansId,
                        culture,
                        updatedFormFields,
                        activity.id,
                    ),
                );

                showToast({
                    body: t('leverandor.successfulOperation'),
                    type: 'success',
                });
            } catch {
                showToast({
                    body: t('general.error.message'),
                    type: 'error',
                });
            }
        },
        [
            activity.id,
            activity.formFields,
            prosessInstansId,
            culture,
            attachComment,
            sendNotification,
            dispatch,
            t,
        ],
    );

    return { replyToComment, isLoading };
};
